import React, { useMemo } from 'react'
import { Card, CardContent, CardHeader, Grid, Box, Backdrop, CircularProgress, Stack, Typography } from '@mui/material'
import { DateTime, DateTimeUnit, Duration, Interval } from 'luxon'
import { useURLParam } from '../../actions/hooks'
import AlertMetricsFilter from './AlertMetricsFilter'
import AlertCountGraph from './AlertCountGraph'
import AlertMetricsTable from './AlertMetricsTable'
import AlertAveragesGraph from './AlertAveragesGraph'
import { GenericError, ObjectNotFound } from '../../error-pages'
import { useWorker } from '../../worker'
import { AlertMetricsOpts } from './useAlertMetrics'
import { useAlerts } from './useAlerts'
import { useQuery } from 'urql'
import Spinner from '../../loading/components/Spinner'
import { AlertSearchOptions } from '../../../schema'

export type AlertMetricsProps = {
  serviceID?: string
  userID?: string
}

const units: Record<string, DateTimeUnit> = {
  P1D: 'day',
  P1W: 'week',
  P1M: 'month',
}

export default function AlertMetrics({
  serviceID,
  userID,
}: AlertMetricsProps): JSX.Element {
  const now = useMemo(() => DateTime.now(), [])

  const [svc] = useQuery({
    query: 'query Svc($id: ID!) {service(id:$id){id,name}}',
    variables: { id: serviceID },
    pause: !serviceID,
  })

  const [usr] = useQuery({
    query: 'query Usr($id: ID!) {user(id:$id){id,name}}',
    variables: { id: userID },
    pause: !userID,
  })
  
  const [range] = useURLParam<string>('range', 'P1M')
  const [ivl] = useURLParam<string>('interval', 'P1D')
  const [serviceIDStr] = useURLParam<string>('services', '')
  const [userIDStr] = useURLParam<string>('users', '')
  const [severityStr] = useURLParam<string>('severities', '')
  const [startStr] = useURLParam<string>('start', '')
  const [endStr] = useURLParam<string>('end', '')

  let since = now
  let until = now
  const unit = units[ivl] || 'day'

  if (range === 'custom' && startStr && endStr) {
    since = DateTime.fromISO(startStr)
    until = DateTime.fromISO(endStr).endOf('day')
  } else if (range !== 'custom') {
    since = now.minus(Duration.fromISO(range)).startOf(unit)
  }

  const graphDur = Duration.fromISO(ivl).toISO()

  const alertOptions: AlertSearchOptions = {
    filterByServiceID: serviceID ? [serviceID] : (serviceIDStr ? serviceIDStr.split(',') : undefined),
    filterByUserID: userID ? [userID] : (userIDStr ? userIDStr.split(',') : undefined),
    notCreatedBefore: since.toISO(),
    createdBefore: until.toISO(),
  }
  
  const depKey = `${serviceID || serviceIDStr}-${userID || userIDStr}-${severityStr}-${since}-${until}`

  const alertsData = useAlerts(alertOptions, depKey)
  const graphInterval = Interval.fromDateTimes(since, until).toISO()

  // useMemo to use same object reference
  const metricsOpts: AlertMetricsOpts = useMemo(
    () => ({ int: graphInterval, dur: graphDur, alerts: alertsData.alerts }),
    [graphInterval, graphDur, alertsData.alerts],
  )

  const [graphData, graphDataStatus] = useWorker(
    'useAlertMetrics',
    metricsOpts,
    [],
  )

  if (svc.fetching || usr.fetching) return <Spinner />
  if (serviceID && !svc.data?.service?.name && !svc.error) return <ObjectNotFound />
  if (userID && !usr.data?.user?.name && !usr.error) return <ObjectNotFound />

  if (alertsData.error) {
    return <GenericError error={alertsData.error.message} />
  }

  return (
    <Grid container spacing={2}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={alertsData.loading || graphDataStatus.loading}
      >
        <Stack alignItems='center' spacing={2}>
          <CircularProgress color='inherit' />
        </Stack>
      </Backdrop>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <AlertMetricsFilter serviceID={serviceID} userID={userID} />
            <CardHeader
              title='Alert Counts'
              component='h2'
              sx={{ ml: '2rem', mb: 0, pb: 0 }}
            />
            <Box sx={{ ml: '2rem', mr: '2rem', mt: 2 }}>
              <AlertCountGraph
                data={graphData}
                loading={graphDataStatus.loading || alertsData.loading}
              />
            </Box>
            <CardHeader
              title='Alert Averages'
              component='h2'
              sx={{ ml: '2rem', mb: 0, pb: 0 }}
            />
            <Box sx={{ ml: '2rem', mr: '2rem', mt: 2 }}>
              <AlertAveragesGraph
                data={graphData}
                loading={graphDataStatus.loading || alertsData.loading}
              />
            </Box>
            <CardHeader
              title='Alert Metrics Data'
              component='h2'
              sx={{ ml: '2rem', mb: 0, pb: 0 }}
            />
            <Box sx={{ ml: '2rem', mr: '2rem', mt: 2 }}>
              <AlertMetricsTable
                alerts={alertsData.alerts}
                serviceName={serviceID ? svc.data?.service?.name : (userID ? usr.data?.user?.name : '')}
                startTime={since.toFormat('yyyy-MM-dd')}
                endTime={until.toFormat('yyyy-MM-dd')}
                loading={graphDataStatus.loading || alertsData.loading}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}
